import { FunctionComponent, useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
    EndStatusEnum,
    getFindingAreaFullNameFromAbbreviation,
    getFindingAreaSubmissionText,
    QuestionSchemaType,
    ResponseSchemaType,
    StepAction,
    StepActionType,
} from "../../utils/surveyStatusSchemas";
import { Page } from "../Page";
import SurveySection from "../../components/SurveySection";
import CustomStepper from "../../components/CustomStepper";
import {
    Text,
    Box,
    Divider,
    Flex, Button, Hide
} from "@chakra-ui/react";

import { useAppState } from "../../state/useAppState";
import { SurveySchemaType } from "../../utils/surveyStatusSchemas";
import { useGetSurvey, useGetAnswers, Answer, useSubmitSurvey, saveConclusion, SaveConclusionArgs, useGetConclusions, ConclusionResponseDto} from "../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import SurveySubmissionModal from "./SurveySubmissionModal";
import {Shimmer} from "am-tax-fe-core";
import {nextQuestionIdOrConclusion} from "../../utils/SurveyExtensions";

export const SurveyPage: FunctionComponent = () => {
    const token = useAppState(state => state.token);
    const [searchParams] = useSearchParams();

    const getAnswersMutation = useGetAnswers();
    const getSurveyMutation = useGetSurvey();
    const submitSurveyMutation = useSubmitSurvey();
    const getConclusions = useGetConclusions();

    const navigate = useNavigate();

    const [surveyMapping, setSurveyMapping] = useState<SurveySchemaType>();
    const getSurveySchema = useAppState(state => state.getSurveySchema);
    const setSurveyId = useAppState(state => state.setSurveyId);
    const [surveyId, setLocalSurveyId] = useState("");
    const [answers, setAnswers] = useState<Answer[]>([]);

    const initialStepState = {
        completedSteps: [] as number[],
        skippedSteps: [] as number[],
        activeStep: 0,
    };

    const [stepState, dispatch] = useReducer(stepReducer, initialStepState);
    const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

    function stepReducer(state: typeof initialStepState, action: StepAction) {
        switch (action.type) {
            case StepActionType.COMPLETE:
                return {
                    ...state,
                    completedSteps: state.completedSteps.includes(action.index)
                        ? state.completedSteps
                        : [...state.completedSteps, action.index],
                    skippedSteps: state.skippedSteps.filter((step) => step !== action.index),
                    activeStep: Math.max(...state.completedSteps.concat(state.skippedSteps)) + 1,
                };
            case StepActionType.SKIP:
                return {
                    ...state,
                    skippedSteps: state.skippedSteps.includes(action.index)
                        ? state.skippedSteps
                        : [...state.skippedSteps, action.index],
                    completedSteps: state.completedSteps.filter((step) => step !== action.index),
                    activeStep: Math.max(...state.completedSteps.concat(state.skippedSteps)) + 1,
                };
            default:
                return state;
        }
    }

    const [surveySubmissionModalOpen, setSubmissionModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const fetchSchema = async () => {
            const surveySchema = await getSurveySchema();
            setSurveyMapping(surveySchema);
        };

        fetchSchema();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, searchParams]);

    // Get survey id based on token in app state when the local token state has been updated from its original value.
    useEffect(() => {
        const fetchSurveyId = async () => {
            if (token !== "") {
                const sid = await getSurveyMutation.mutateAsync();
                setSurveyId(sid.id!);
                setLocalSurveyId(sid.id!);
            }
        };

        fetchSurveyId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Get survey responses when surveyId is changed from the original value.
    useEffect(() => {
        if (surveyId !== ""){
            (async () => {
                const response: Answer[] = await getAnswersMutation.mutateAsync({
                    surveyId: surveyId
                });

                setAnswers(response);
                await getInitialConclusionStates();
                setLoading(false);
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyId]);

    const scrollToSection = (index: number) => {
        // TO DO, review the scenario where the first ref is not always scrolled to and then uncomment this function.
        console.log("Scrolling to section", index);
        // const section = sectionRefs.current[index];
        // if (section) {
        //     section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // }
    };

    async function getInitialConclusionStates() {
        const conclusions: ConclusionResponseDto[] = await getConclusions.mutateAsync({
            surveyId: surveyId
        });

         surveyMapping?.findingAreas.forEach((findingArea, index) => {
            const conclusion: ConclusionResponseDto | undefined = conclusions.find(item => getFindingAreaSubmissionText(findingArea.id) === item.findingArea);
            if (conclusion !== undefined) {
                const actionType: StepActionType = conclusion.isSkipped && StepActionType.SKIP || StepActionType.COMPLETE;
                dispatch({ type: actionType, index });
            }
        });

    }

    const constructConclusion = useCallback(
        (findingAreaId: string, lastAnswerId: string, branchingPath: string[], conclusionResult: string | undefined): SaveConclusionArgs => {
            return {
                surveyId: surveyId,
                findingArea: getFindingAreaFullNameFromAbbreviation(findingAreaId),
                riskRating: conclusionResult,
                isSkipped: conclusionResult === undefined,
                lastAnswerId: lastAnswerId ? lastAnswerId : undefined,
                branchingPath: branchingPath
            };
        },
        [surveyId]
    );

    const handleSurveyCompletion = useCallback(
        (index: number, sectionStatus: EndStatusEnum, findingAreaId: string, lastAnswerId: string,  branchingPath: string[], conclusionResult? : string | undefined) => {
            if (sectionStatus === EndStatusEnum.COMPLETED) {
                dispatch({ type: StepActionType.COMPLETE, index });
                if (conclusionResult !== undefined) {
                    saveConclusion(constructConclusion(findingAreaId, lastAnswerId, branchingPath, conclusionResult));
                }
            } else if (sectionStatus === EndStatusEnum.SKIPPED) {
                dispatch({ type: StepActionType.SKIP, index });
                saveConclusion(constructConclusion(findingAreaId, lastAnswerId, new Array<string>, undefined));
            }
        },
        [dispatch, constructConclusion]
    );

    const findNextQuestion = (questions:Array<QuestionSchemaType> ,id: string): QuestionSchemaType | undefined => {
        const nextQuestion = questions.find(q => q.id === id);
        if (!nextQuestion) {
            console.warn(`No question found for ID: ${id}`);
            return undefined;
        }
        return nextQuestion;
    };

    const getQuestionsForFindingArea = (findingArea: string): Array<QuestionSchemaType> => {
        return surveyMapping?.findingAreas?.find(fa => fa.id === findingArea)?.questions || [];
    }

    function getInitialFindingAreaStack(findingArea: string) : Array<ResponseSchemaType> {
        const questions = getQuestionsForFindingArea(findingArea);
        const initialStack: Array<ResponseSchemaType> = [];
        let nextQID = questions[0].id;

        questions.forEach(question => {
            if (nextQID === question.id) {
                const foundAnswer = answers.find(a => a.questionId === question.id);
                if (foundAnswer != undefined) {
                    const response = foundAnswer.answerText!;
                    initialStack.push({
                        question: question,
                        response: response
                    })
                    nextQID = nextQuestionIdOrConclusion(question, response);
                }
            }
        });

        let nextQuestion = questions[0];
        const lastAnswer = initialStack[initialStack.length - 1];
        let anotherQuestion = true;

        if (lastAnswer !== undefined) {
            const next_id = nextQuestionIdOrConclusion(lastAnswer.question, lastAnswer.response);
            nextQuestion = findNextQuestion(questions, next_id)!,
                anotherQuestion = !next_id.includes("END_")
        }

        if (anotherQuestion && nextQuestion) {
            initialStack.push({
                question: nextQuestion,
                response: ""
            })
        }

        return initialStack;
    }

    const isSubmitDisabled = useCallback(() => {
        const totalSteps = surveyMapping?.findingAreas.length || 0;
        const { completedSteps, skippedSteps } = stepState;

        // Check if the sum of completed and skipped steps matches total sections
        return completedSteps.length + skippedSteps.length !== totalSteps;
    }, [surveyMapping, stepState]);

    const submitIfValid = () => {
        //TODO: Add logic here for checking validation before calling submission modal
        setSubmissionModalOpen(true);
    };

    const handleSubmission = async (surveyId: string) => {
        await submitSurveyMutation.mutateAsync({surveyId});
        navigate('Submitted', { state: { surveyId: surveyId } });
    }

    return (
        <Page
            title={``}
            footer={(<></>)}
        >
            <Shimmer isLoading={loading}>
                {!loading &&
                    <Flex padding={["0.5rem", "1rem", "2rem"]} flexDirection="row" gap="3rem">
                        <Box width={["100%", "100%", "70%"]} overflowY="visible">
                            <Flex display="flex" flexFlow="column nowrap" gap={"2rem"}>
                                <Box>
                                    <Text variant="sectionHeader">
                                       Before you begin:
                                    </Text>
                                    <Text variant="body1">
                                        Please fill out the form to the best of your knowledge. This link can be shared with
                                        your team, who will have full access and can edit all fields. Communication is
                                        key to ensure nothing gets overwritten by multiple people editing at the same time.
                                    </Text>
                                </Box>

                                <Divider />
                                
                                {surveyMapping?.findingAreas.map((item, index) => (
                                    <SurveySection
                                        key={index.toString()}
                                        ref={(el) => (sectionRefs.current[index] = el)}
                                        findingArea={item}
                                        completionHandler={handleSurveyCompletion}
                                        surveyIndex={index}
                                        initialStack={getInitialFindingAreaStack(item.id)}
                                        isSkipped={stepState.skippedSteps.includes(index)}
                                    />
                                ))}
                            </Flex>
                            <Flex justifyContent="flex-end" mt="2rem">
                                <Button
                                    variant="solid"
                                    colorScheme="blue"
                                    isDisabled={isSubmitDisabled()}
                                    onClick={() => submitIfValid()}
                                >
                                    Submit and Close
                                </Button>
                            </Flex>
                            <SurveySubmissionModal isLoading={submitSurveyMutation.isPending}isOpen={surveySubmissionModalOpen} onClose={() => setSubmissionModalOpen(false) } onSubmit={() => {
                                handleSubmission(surveyId);
                            }}></SurveySubmissionModal>
                        </Box>

                        <Hide below="md">
                            <Box>
                                <CustomStepper
                                    stepList={surveyMapping?.findingAreas.map((item) => item.name)}
                                    activeStep={stepState.activeStep}
                                    completedSteps={stepState.completedSteps}
                                    skippedSteps={stepState.skippedSteps}
                                    onStepClick={(index) => scrollToSection(index)}
                                />
                            </Box>
                        </Hide>
                    </Flex>
                }
            </Shimmer>
        </Page>
    );
};
